import { Link } from 'react-router-dom';
import {
  ArrowRight, Search, PenTool, Palette, Calculator,
  Layers, ClipboardCheck, Package, Ship, Wrench
} from 'lucide-react';
import { FadeInUp } from '../components/animations';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schema';

const ServicesPage = () => {
  const services = [
    { icon: <Search size={32} strokeWidth={1.5} />, title: 'Supplier Identification', description: 'We identify and evaluate suitable Indian manufacturers based on your specific product requirements, quality expectations and commercial parameters.' },
    { icon: <PenTool size={32} strokeWidth={1.5} />, title: 'Product Development', description: 'We coordinate product development with manufacturers, managing technical specifications, material requirements and design details.' },
    { icon: <Palette size={32} strokeWidth={1.5} />, title: 'Sampling', description: 'We manage the sample development process, coordinating between buyer requirements and manufacturer capabilities until approval.' },
    { icon: <Calculator size={32} strokeWidth={1.5} />, title: 'Quotation Comparison', description: 'We obtain and present competitive quotations from shortlisted manufacturers, helping you compare options and make informed decisions.' },
    { icon: <Layers size={32} strokeWidth={1.5} />, title: 'Production Coordination', description: 'During production, we follow up on timelines, quality and requirements. Regular updates keep you informed throughout the process.' },
    { icon: <ClipboardCheck size={32} strokeWidth={1.5} />, title: 'Quality Control', description: 'We coordinate quality inspections at key production stages to ensure the final product meets agreed specifications and standards.' },
    { icon: <Package size={32} strokeWidth={1.5} />, title: 'Packaging Coordination', description: 'We coordinate packaging requirements with manufacturers, ensuring products are packed as per buyer specifications for safe shipping.' },
    { icon: <Ship size={32} strokeWidth={1.5} />, title: 'Shipment Coordination', description: 'We manage export documentation, customs requirements and logistics coordination to deliver products to your destination.' },
    { icon: <Wrench size={32} strokeWidth={1.5} />, title: 'Custom Sourcing', description: 'For products outside standard categories, we evaluate requirements and work to identify suitable manufacturing options in India.' },
  ];

  return (
    <div className="pt-20" data-testid="services-page">
      <SEO
        title="Sourcing Services | Taiga Sourcing"
        description="End-to-end sourcing services — supplier identification, product development, sampling, production coordination, quality control and shipment from India."
        keywords="sourcing services India, India sourcing agent services, production coordination India, quality control India"
        path="/services"
        schema={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Services', path: '/services' }])}
      />
      <section className="relative py-24 lg:py-32 bg-[#071E45] text-white" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <p className="text-sm tracking-[0.3em] uppercase text-white/50 mb-4 font-medium">What We Do</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="services-title">
              OUR SERVICES
            </h1>
            <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
              End-to-end sourcing coordination from requirement to shipment across multiple product categories.
            </p>
          </FadeInUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <FadeInUp key={i} delay={i * 0.05}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="p-8 bg-[#fafaf9] border border-[#E5E0D8]/60 hover:shadow-md transition-all"
                  data-testid={`service-card-${i}`}
                >
                  <div className="text-[#1E3D66] mb-5">{service.icon}</div>
                  <h3 className="text-base font-bold tracking-wide text-[#0B2A57] mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#6B7280] leading-relaxed">{service.description}</p>
                </motion.div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#071E45] text-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              NEED SOURCING SUPPORT?
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8">
              Share your requirement and let us manage the sourcing process in India.
            </p>
            <Link
              to="/request-sourcing"
              className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0B2A57] text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-xl hover:-translate-y-0.5"
              data-testid="services-cta-button"
            >
              Request Sourcing <ArrowRight size={18} />
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
